package com.essntl.components.sidepanel

import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.Layout
import androidx.compose.ui.layout.Placeable
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.coerceAtLeast
import androidx.compose.ui.unit.dp
import com.essntl.uikit.components.scroll.EsHorizontalScrollbar
import com.essntl.uikit.components.utils.EsConstants
import kotlin.math.max
import kotlin.math.roundToInt

data class SizeConstraint(
    val minSize: Dp = 0.dp,
    val maxSize: Dp = Dp.Infinity
) {
    init {
        require(minSize >= 0.dp) { "minSize must be >= 0.dp" }
        require(maxSize >= minSize) { "maxSize must be >= minSize" }
    }
}

internal expect fun Modifier.cursorForHorizontalResize(): Modifier

private fun Constraints.maxByDirection(isHorizontal: Boolean): Int = if (isHorizontal) maxWidth else maxHeight
private fun Placeable.valueByDirection(isHorizontal: Boolean): Int = if (isHorizontal) width else height
private fun Constraints.withUnconstrainedWidth() = copy(minWidth = 0, maxWidth = Constraints.Infinity)
private fun Constraints.withUnconstrainedHeight() = copy(minHeight = 0, maxHeight = Constraints.Infinity)

@Composable
fun SidePanel(
    modifier: Modifier = Modifier,
    isHorizontal: Boolean,
    sidePanelState: SidePanelState,
    splitter: Splitter,
    lazyListState: LazyListState = rememberLazyListState(),
    horizontalScroll: Boolean = true,
    showScrollbars: Boolean = true,
    sidePanel: (@Composable () -> Unit),
    content: (@Composable BoxScope.() -> Unit),
) {
    val localDensity = LocalDensity.current

    var width by remember {
        mutableStateOf(0.dp)
    }

    Layout(
        content = {
            Box {
                if (horizontalScroll) {
                    LazyRow(
                        state = lazyListState,
                        modifier = Modifier
                            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
                            .fillMaxSize()
                    ) {
                        item {
                            Box(
                                content = content,
                                modifier = Modifier
                                    .width(width)
                                    .fillMaxHeight()
                            )
                        }
                    }

                    if (lazyListState.canScrollBackward)
                        Box(
                            modifier = Modifier
                                .align(Alignment.CenterStart)
                                .fillMaxHeight()
                                .width(3.dp)
                                .background(
                                    brush = Brush.horizontalGradient(
                                        colors = listOf(
                                            Color.White.copy(alpha = .5f),
                                            Color.Transparent,
                                        )
                                    )
                                )
                                .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
                        )

                    if (showScrollbars)
                        EsHorizontalScrollbar(
                            lazyListState = lazyListState,
                            modifier = Modifier
                                .align(Alignment.BottomCenter)
                                .fillMaxWidth()
                        )
                } else {
                    content()
                }
            }

            if (sidePanelState.sidePanelOpened) {
                Box {
                    splitter.measuredPart()
                }

                Box(
                    modifier = if (sidePanelState.isResizing) {
                        Modifier.cursorForHorizontalResize()
                    } else {
                        Modifier
                    }
                ) {
                    sidePanel()
                }

                Box(
                    modifier = Modifier
                        .cursorForHorizontalResize()
                        .pointerInput(sidePanelState) {
                            detectDragGestures(
                                onDragStart = { sidePanelState.dispatchDragStart() },
                                onDrag = { change, _ ->
                                    change.consume()
                                    sidePanelState.dispatchRawMovement(
                                        (if (isHorizontal) change.position.x else change.position.y).toDp()
                                    )
                                },
                                onDragEnd = sidePanelState::dispatchDragEnd,
                                onDragCancel = sidePanelState::dispatchDragEnd,
                            )
                        }
                ) {
                    splitter.handlePart()
                }
            }
        },
        modifier = modifier
            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
            .onSizeChanged {
                val newWidth = with(localDensity) {
                    it.width.toDp().coerceAtLeast(1200.dp)
                }

                if (newWidth != width)
                    width = newWidth
            }
    ) { measurables, constraints ->
        with(sidePanelState) {
            val contentMeasurable = measurables[0]

            val maxConstraintOnMainAxis = constraints.maxByDirection(isHorizontal)

            if (measurables.size == 1) {
                val contentPlaceable = contentMeasurable.measure(
                    if (isHorizontal) {
                        constraints.copy(
                            minWidth = 0,
                            maxWidth = maxConstraintOnMainAxis
                        )
                    } else {
                        constraints.copy(
                            minHeight = 0,
                            maxHeight = maxConstraintOnMainAxis
                        )
                    }
                )

                return@Layout layout(constraints.maxWidth, contentPlaceable.height) {
                    contentPlaceable.place(0, 0)
                }
            }

            val sidePanelMinimalSizePx = panelSizeConstraint.minSize.value * density
            val sidePanelMaximalSizePx = panelSizeConstraint.maxSize.value * density

            val splitterMeasurable = measurables[1]
            val sidePanelMeasurable = measurables[2]
            val handleMeasurable = measurables[3]

            // Need the size of the splitter to determine the min/max position
            // Constrain the splitter only on the "other" axis
            val splitterConstraints =
                if (isHorizontal)
                    constraints.withUnconstrainedWidth()
                else
                    constraints.withUnconstrainedHeight()
            val splitterPlaceable = splitterMeasurable.measure(splitterConstraints)
            val splitterSize = splitterPlaceable.valueByDirection(isHorizontal)

            val constrainedMin = (maxConstraintOnMainAxis - sidePanelMaximalSizePx - splitterSize)
                .coerceAtLeast(0f)
            val constrainedMax = (maxConstraintOnMainAxis - sidePanelMinimalSizePx - splitterSize)
                .coerceAtLeast(constrainedMin)

            parentMaxSize = maxConstraintOnMainAxis.toDp()

            val sidePanelSizePx = sidePanelSize.value * density

            val contentPlaceablePosition = (maxConstraintOnMainAxis - sidePanelSizePx - splitterSize)
                .coerceIn(constrainedMin, constrainedMax)
                .roundToInt()

            val contentPlaceable = contentMeasurable.measure(
                if (isHorizontal) {
                    constraints.copy(
                        minWidth = 0,
                        maxWidth = contentPlaceablePosition
                    )
                } else {
                    constraints.copy(
                        minHeight = 0,
                        maxHeight = contentPlaceablePosition
                    )
                }
            )

            val sidePanelPlaceablePosition = contentPlaceablePosition + splitterSize
            val secondAvailableSize = (maxConstraintOnMainAxis - sidePanelPlaceablePosition).coerceAtLeast(0)

            val sidePanelPlaceable = sidePanelMeasurable.measure(
                if (isHorizontal) {
                    constraints.copy(
                        minWidth = 0,
                        maxWidth = secondAvailableSize
                    )
                } else {
                    constraints.copy(
                        minHeight = 0,
                        maxHeight = secondAvailableSize
                    )
                }
            )

            val handlePlaceable = handleMeasurable.measure(splitterConstraints)
            val handleSize = handlePlaceable.valueByDirection(isHorizontal)

            val handlePosition = when (splitter.alignment) {
                SplitterHandleAlignment.Before -> contentPlaceablePosition + splitterSize - handleSize
                SplitterHandleAlignment.Above -> contentPlaceablePosition + (splitterSize - handleSize) / 2
                SplitterHandleAlignment.After -> contentPlaceablePosition
            }

            val height = max(contentPlaceable.height, sidePanelPlaceable.height)

            layout(constraints.maxWidth, height) {
                contentPlaceable.place(0, 0)
                if (isHorizontal) {
                    sidePanelPlaceable.place(sidePanelPlaceablePosition, 0)
                    splitterPlaceable.place(contentPlaceablePosition, 0)
                    if (moveEnabled) {
                        handlePlaceable.place(handlePosition, 0)
                    }
                } else {
                    sidePanelPlaceable.place(0, sidePanelPlaceablePosition)
                    splitterPlaceable.place(0, contentPlaceablePosition)
                    if (moveEnabled) {
                        handlePlaceable.place(0, handlePosition)
                    }
                }
            }
        }
    }
}